import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { PrimaryButton } from '../../../components/common/buttons/PrimaryButton';
import FormSectionTitle from '../../../components/common/formSectionTitle/FormSectionTitle';
import { LabeledStateInput } from '../../../components/common/inputs/LabeledStateInput';
import PlusIcon from '../../../components/icons/PlusIcon';
import { useTrackUnsavedChange } from '../../../context/FormChangeProvider';
import getInputState from '../../../utils/getInputState/getInputState';

interface UpdateMobileAppVersionFormProps {
  currentVersion: string;
  onSubmitForm: ({ data }: { data: IFormInput }) => Promise<void>;
}

type IFormInput = {
  version: string;
};

const updateMobileAppVersionValidationSchema = yup.object({
  version: yup
    .string()
    .matches(/^\d+\.\d+\.\d+$/, { message: 'mobileAppVersion.invalidVersionFormat' })
    .required('validationMessages.required'),
});

const UpdateMobileAppVersionForm = ({ currentVersion, onSubmitForm }: UpdateMobileAppVersionFormProps) => {
  const { t } = useTranslation();
  const defaultValues: IFormInput = {
    version: currentVersion,
  };

  const newVersion = useForm<IFormInput>({
    mode: 'onSubmit',
    resolver: yupResolver(updateMobileAppVersionValidationSchema),
    defaultValues,
  });

  useTrackUnsavedChange(newVersion.formState.isDirty);

  const onSubmit: SubmitHandler<IFormInput> = async data => {
    if (data.version === currentVersion) {
      return;
    }

    onSubmitForm({ data });

    newVersion.reset(data);
  };

  return (
    <FormProvider {...newVersion}>
      <div className="bg-white900 pl-8 pb-20">
        <FormSectionTitle className="mb-6 mt-10" title={t('mobileAppVersion.updateFormTitle')} />
        <LabeledStateInput
          id={`mobile-app-version-input`}
          state={getInputState(newVersion.formState.errors?.version?.message)}
          labeledProps={{
            label: t('mobileAppVersion.versionLabel'),
            errorMessage: t(`errorMessages.${newVersion.formState.errors.version?.message}`),
            wrapperClassName: 'w-full',
          }}
          inputProps={{
            placeholder: '1.0.0',
            register: {
              ...newVersion.register(`version`),
            },
            autoComplete: 'off',
          }}
        />
        <PrimaryButton
          onClick={newVersion.handleSubmit(onSubmit)}
          disabled={!newVersion.formState.isDirty || !!newVersion.formState.errors.version}
          className="w-fit self-end mt-8"
          prefixIcon={<PlusIcon />}
        >
          {t('mobileAppVersion.updateVersionButton')}
        </PrimaryButton>
      </div>
    </FormProvider>
  );
};

export default UpdateMobileAppVersionForm;
